import CustomUploadAdapterPlugin from './plugins/custom_uploader';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';

const editorLicenseConfig = {
  licenseKey: 'GPL',
};

const attachPlainPastedImageHandler = editor => {
  const clipboardPipeline = editor.plugins.get('ClipboardPipeline');

  clipboardPipeline.on('inputTransformation', (event, data) => {
    const isExternalPaste = data.method === 'paste' && data.sourceEditorId !== editor.id;

    if (!isExternalPaste || !data.content) {
      return;
    }

    editor.editing.view.change(writer => {
      for (const item of editor.editing.view.createRangeIn(data.content).getItems()) {
        if (item.is('element', 'img')) {
          writer.removeAttribute('width', item);
          writer.removeAttribute('height', item);
          writer.removeAttribute('style', item);
        }

        if (item.is('element', 'figure') && item.hasClass('image')) {
          if (item.hasClass('image_resized')) {
            writer.removeClass('image_resized', item);
          }

          writer.removeAttribute('style', item);
        }
      }
    });
  });
};

const basicToolbarItems = [
  'undo', 'redo',
  '|',
  'heading',
  '|',
  'fontsize', 'fontColor', 'fontBackgroundColor',
  '|',
  'bold', 'italic', 'underline', 'strikethrough',
  '|',
  'link', 'insertTable',
  '|',
  'alignment',
  '|',
  'bulletedList', 'numberedList', 'outdent', 'indent',
];

const imageToolbarItems = [
  'undo', 'redo',
  '|',
  'heading',
  '|',
  'fontsize', 'fontColor', 'fontBackgroundColor',
  '|',
  'bold', 'italic', 'underline', 'strikethrough',
  '|',
  'link', 'uploadImage', 'insertTable',
  '|',
  'alignment',
  '|',
  'bulletedList', 'numberedList', 'outdent', 'indent',
];

const fullToolbarItems = [
  'undo', 'redo',
  '|',
  'heading',
  '|',
  'fontsize', 'fontColor', 'fontBackgroundColor',
  '|',
  'bold', 'italic', 'underline', 'strikethrough',
  '|',
  'link', 'uploadImage', 'insertTable', 'blockQuote', 'mediaEmbed',
  '|',
  'alignment',
  '|',
  'bulletedList', 'numberedList', 'outdent', 'indent',
];

/*
* wysiwyg_editor
*/
export class WysiwygEditor {
  constructor() {
    DecoupledEditor
      .create( document.querySelector( '#jsWysiwygEditor' ),{
        ...editorLicenseConfig,
        toolbar: {
          items: basicToolbarItems
        },
        removePlugins: ['MediaEmbed', 'ImageUpload', 'BlockQuote', 'EasyImage','FontFamily'],
        heading: {
          options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
            { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
            { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
          ]
        },
        ui: {
          viewportOffset: {
            top: 55
          }
        },

        link: {
          addTargetToExternalLinks: true
        },
      })
      .then(editor => {
        const toolbarContainer = document.querySelector( '#toolbar-container' );
        toolbarContainer.appendChild( editor.ui.view.toolbar.element );

        // これまでClassic Editorを使っていたが、使える機能に限りがあったため、Document Editorに移行した
        // Classic Editorの場合、画面に描画するタグとサーバーにデータを送付するためのタグは同一で動作したが、
        // Document editorの場合、それぞれのタグを分ける必要がある（分けないとtoolbarが表示されなくなる）
        // 以下の3行のコードは、画面に描画されているデータをサーバーに送付するためのhidden inputタグに書くのするための処理
        editor.model.document.on('change:data', () => {
          document.getElementById('hiddenContent').value = editor.getData();
        });
      })
      .catch( error => {
        console.error( error );
      });
  }
}

export class WysiwygImageEditor {
  constructor() {
    DecoupledEditor
      .create( document.querySelector( '#jsWysiwygImageEditor' ),{
        ...editorLicenseConfig,
        extraPlugins: [ CustomUploadAdapterPlugin ],
        toolbar: {
          items: imageToolbarItems
        },
        removePlugins: ['MediaEmbed', 'BlockQuote','FontFamily'],
        heading: {
          options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
            { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
            { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
          ]
        },
        ui: {
          viewportOffset: {
            top: 55
          }
        },
        link: {
          addTargetToExternalLinks: true
        },
      })
      .then(editor => {
        attachPlainPastedImageHandler(editor);

        const toolbarContainer = document.querySelector( '#toolbar-container' );
        toolbarContainer.appendChild( editor.ui.view.toolbar.element );

        editor.model.document.on('change:data', () => {
          document.getElementById('hiddenContent').value = editor.getData();
        });
      })
      .catch( error => {
        console.error( error );
      });
  }
}

export class WysiwygFullEditor {
  constructor() {
    DecoupledEditor
      .create( document.querySelector( '#jsWysiwygFullEditor' ),{
        ...editorLicenseConfig,
        extraPlugins: [ CustomUploadAdapterPlugin ],
        mediaEmbed: {
          previewsInData: true
        },
        toolbar: {
          items: fullToolbarItems
        },
        removePlugins: ['FontFamily'],
        heading: {
          options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
            { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
            { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
          ]
        },
        ui: {
          viewportOffset: {
            top: 55
          }
        },
        link: {
          addTargetToExternalLinks: true
        }
      })
      .then(editor => {
        attachPlainPastedImageHandler(editor);

        const toolbarContainer = document.querySelector( '#toolbar-container' );
        toolbarContainer.appendChild( editor.ui.view.toolbar.element );

        editor.model.document.on('change:data', () => {
          document.getElementById('hiddenContent').value = editor.getData();
        });
      })
      .catch( error => {
        console.error( error );
      });
  }
}

export class PrivacyConsentWysiwygEditor {
  constructor() {
    // プライバシー同意チェックボックスの切り替え機能
    const privacyConsentEnabled = document.getElementById('privacy_consent_enabled');
    const privacyConsentWrapper = document.getElementById('privacy_consent_content_wrapper');

    if (privacyConsentEnabled && privacyConsentWrapper) {
      let privacyConsentEditor = null;

      // CKEditor初期化関数
      const initPrivacyConsentEditor = () => {
        if (privacyConsentEditor) {
          return; // 既に初期化済み
        }

        DecoupledEditor
          .create(document.querySelector('#jsPrivacyConsentWysiwygEditor'), {
            ...editorLicenseConfig,
            toolbar: {
              items: basicToolbarItems
            },
            removePlugins: ['MediaEmbed', 'ImageUpload', 'BlockQuote', 'EasyImage','FontFamily'],
            heading: {
              options: [
                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
              ]
            },
            ui: {
              viewportOffset: {
                top: 55
              }
            },
            link: {
              addTargetToExternalLinks: true
            }
          })
          .then(editor => {
            privacyConsentEditor = editor;
            const toolbarContainer = document.querySelector('#jsPrivacyConsentToolbarContainer');
            toolbarContainer.appendChild(editor.ui.view.toolbar.element);

            editor.model.document.on('change:data', () => {
              document.getElementById('jsPrivacyConsentHiddenContent').value = editor.getData();
            });
          })
          .catch(error => {
            console.error(error);
          });
      };

      // 初期状態の設定
      if (privacyConsentEnabled.checked) {
        privacyConsentWrapper.style.display = 'block';
        initPrivacyConsentEditor();
      }

      // チェックボックス変更時の処理
      privacyConsentEnabled.addEventListener('change', function() {
        if (this.checked) {
          privacyConsentWrapper.style.display = 'block';
          initPrivacyConsentEditor();
        } else {
          privacyConsentWrapper.style.display = 'none';
        }
      });
    }
  }
}
